import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import ThinScroll from "../shared/ThinScroll";
import { getCiCompetitorDetail } from "../../api/competitiveIntelligence";
import { InferredOccupancy, categoryLabel } from "../../utils/ciFormat";
import { MONTHS, fmtRate } from "./shared";
import useDrawerDialog from "../../hooks/useDrawerDialog";

const fmtAddedDate = (iso) => {
  if (!iso) return null;
  const ymd = iso.slice(0, 10);
  const [year, month, day] = ymd.split("-");
  if (!year || !month || !day) return null;
  return `${MONTHS[parseInt(month, 10) - 1]} ${parseInt(day, 10)}, ${year}`;
};

const fmtMiles = (value) => (value == null ? "—" : `${Number(value).toFixed(1)} mi`);
const fmtRating = (value) => (value == null ? "—" : Number(value).toFixed(1));
const fmtReviewCount = (value) => {
  if (value == null) return "";
  const count = Number(value);
  if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(count);
};

const SOURCE_LABELS = {
  campspot: "Campspot",
  airbnb: "Airbnb",
  hotel: "Hotel",
  hipcamp: "Hipcamp",
  glampinghub: "GlampingHub",
};
const sourceLabel = (source) => SOURCE_LABELS[source] ?? (source ? source.charAt(0).toUpperCase() + source.slice(1) : "—");

// Source pills follow the spec tier palette where it applies:
//   hotel  → spec blue  (#3B82F6)
//   airbnb → spec pink  (#EC4899)
// glampinghub keeps violet-500 (= spec purple #8B5CF6, valid). campspot and
// hipcamp are platform markers without a tier mapping; their non-spec hues are
// retained as platform identity until the spec covers them.
const SOURCE_PILL = {
  campspot:    "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20",
  airbnb:      "bg-pink-50 text-pink-700 ring-pink-200 dark:bg-pink-500/10 dark:text-pink-300 dark:ring-pink-500/20",
  hipcamp:     "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
  glampinghub: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
  hotel:       "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20",
};
const sourcePillClass = (source) =>
  SOURCE_PILL[source] ?? "bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700";

const StatusBadge = ({ status, firstDataAt }) => {
  if (status === "active") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Active
      </span>
    );
  }
  if (status === "pending") {
    const added = fmtAddedDate(firstDataAt);
    return (
      <div className="flex flex-col items-end gap-0.5">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          Awaiting Data
        </span>
        {added && <span className="text-[10px] text-gray-500 dark:text-gray-400">Added {added}</span>}
      </div>
    );
  }
  if (status === "no_source") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
        Not on supported platform
      </span>
    );
  }
  return null;
};

const StarIcon = ({ className = "w-3.5 h-3.5 text-amber-500" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 2l2.9 6.93 7.1.62-5.4 4.74L18.18 22 12 18.27 5.82 22l1.58-7.71L2 9.55l7.1-.62L12 2z" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const headlineSummary = (rateSummary) =>
  Array.isArray(rateSummary) && rateSummary.length > 0 ? rateSummary[0] : null;

const fmtChartTickRate = (value) => `$${Math.round(value)}`;

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md bg-white dark:bg-gray-900 ring-1 ring-gray-200 dark:ring-gray-700 shadow-lg px-3 py-2 text-xs">
      <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">{label}</div>
      {payload.map((payloadItem) => (
        <div key={payloadItem.dataKey} className="flex items-center gap-2 tabular-nums">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: payloadItem.color }} />
          <span className="text-gray-500 dark:text-gray-400">{payloadItem.name}</span>
          <span className="ml-auto font-medium text-gray-900 dark:text-gray-100">${Number(payloadItem.value).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
};

const CompetitorDetailDrawer = ({ open, onClose, competitor, clientId }) => {
  const [detail, setDetail] = useState({ loading: false, error: "", data: null });

  // This drawer already declared role="dialog"/aria-modal but stayed tabbable when
  // closed and never trapped focus; the hook adds `inert`, the trap, and focus
  // restore on top of the Escape handling it already had.
  const { containerRef, closeButtonRef } = useDrawerDialog(open, onClose);

  // Body scroll lock while drawer is open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Fetch detail when a new competitor is selected. The chart section only renders
  // when the backend returns time-series rate data.
  useEffect(() => {
    if (!open || !competitor || !clientId || !competitor.atlas_park_id) {
      setDetail({ loading: false, error: "", data: null });
      return undefined;
    }
    let cancelled = false;
    setDetail({ loading: true, error: "", data: null });
    getCiCompetitorDetail(clientId, { atlasParkId: competitor.atlas_park_id })
      .then((resp) => {
        if (!cancelled) setDetail({ loading: false, error: "", data: resp?.data ?? null });
      })
      .catch((err) => {
        if (!cancelled) setDetail({ loading: false, error: err?.message || "Failed to load detail", data: null });
      });
    return () => { cancelled = true; };
  }, [open, competitor, clientId]);

  const selectedCompetitor = competitor;
  const status = selectedCompetitor?.data_status;
  const isActive = status === "active";
  const summary = isActive ? headlineSummary(selectedCompetitor?.rate_summary) : null;
  const location = selectedCompetitor ? [selectedCompetitor.competitor_city, selectedCompetitor.competitor_state].filter(Boolean).join(", ") : "";
  const source = selectedCompetitor?.competitor_source || selectedCompetitor?.atlas_source_name;

  // Use real time-series if backend returns it; otherwise the chart section is hidden.
  const history = Array.isArray(detail.data) ? detail.data : [];

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        aria-hidden="true"
      />
      <aside
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Competitor detail"
        className={`fixed inset-x-0 bottom-0 top-0 md:inset-y-0 md:right-0 md:left-auto w-full md:w-[520px] md:max-w-[calc(100vw-60px)] z-50 bg-white dark:bg-gray-900 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {selectedCompetitor && (
          <>
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {selectedCompetitor.competitor_name || "—"}
                </h2>
                <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-x-2 gap-y-1 flex-wrap">
                  {selectedCompetitor.competitor_google_rating != null && (
                    <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200">
                      <StarIcon className="w-3 h-3 text-amber-500" />
                      <span className="font-medium tabular-nums">{fmtRating(selectedCompetitor.competitor_google_rating)}</span>
                      {selectedCompetitor.competitor_google_review_count != null && (
                        <span className="text-gray-500 dark:text-gray-400">({fmtReviewCount(selectedCompetitor.competitor_google_review_count)})</span>
                      )}
                    </span>
                  )}
                  {selectedCompetitor.distance_miles != null && (
                    <>
                      <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
                      <span>{fmtMiles(selectedCompetitor.distance_miles)}</span>
                    </>
                  )}
                  {location && (
                    <>
                      <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
                      <span>{location}</span>
                    </>
                  )}
                  <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>
                  <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ring-1 ${sourcePillClass(source)}`}>
                    {sourceLabel(source)}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-2 flex-shrink-0">
                <StatusBadge status={status} firstDataAt={selectedCompetitor.first_data_at} />
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]/40 transition"
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <ThinScroll className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
              {isActive && summary && (
                <section>
                  <h3 className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 mb-2">
                    Overall Performance
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg ring-1 ring-gray-200 dark:ring-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
                      <div className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">Avg Rate</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{fmtRate(summary.avg_rate)}</div>
                    </div>
                    <div className="rounded-lg ring-1 ring-gray-200 dark:ring-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
                      <div className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">Avg Occupancy</div>
                      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                        <InferredOccupancy value={summary.avg_occupancy} />
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {isActive && history.length > 0 && (
                <section>
                  <div className="flex items-baseline justify-between mb-2">
                    <h3 className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
                      Rate History (30 days)
                    </h3>
                    <div className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#3B82F6" }} />
                        You
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-400 dark:bg-gray-500" />
                        Competitor
                      </span>
                    </div>
                  </div>
                  <div className="rounded-lg ring-1 ring-gray-200 dark:ring-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
                    <div style={{ width: "100%", height: 180 }} className="rotate-x-labels">
                      <ResponsiveContainer>
                        <LineChart data={history} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 10, fill: "rgb(107,114,128)" }}
                            axisLine={{ stroke: "rgba(148,163,184,0.3)" }}
                            tickLine={false}
                            interval={4}
                          />
                          <YAxis
                            tickFormatter={fmtChartTickRate}
                            tick={{ fontSize: 10, fill: "rgb(107,114,128)" }}
                            axisLine={false}
                            tickLine={false}
                            width={40}
                          />
                          <Tooltip content={<ChartTooltip />} />
                          <Line
                            type="monotone"
                            dataKey="you"
                            name="You"
                            stroke="#3B82F6"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="competitor"
                            name="Competitor"
                            stroke="#9ca3af"
                            strokeWidth={2}
                            strokeDasharray="4 3"
                            dot={false}
                            activeDot={{ r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </section>
              )}

              {isActive && Array.isArray(selectedCompetitor.rate_summary) && selectedCompetitor.rate_summary.length > 0 && (
                <section>
                  <h3 className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 mb-2">
                    Rate Summary by Category
                  </h3>
                  <ThinScroll className="rounded-lg ring-1 ring-gray-200 dark:ring-gray-800 overflow-x-auto responsive-card-table-wrapper">
                    <table className="w-full text-sm min-w-[420px] responsive-card-table">
                      <thead className="bg-gray-50 dark:bg-gray-800/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Category</th>
                          <th className="px-3 py-2 text-right font-medium">Avg Rate</th>
                          <th className="px-3 py-2 text-right font-medium">Avg Occupancy</th>
                          <th className="px-3 py-2 text-right font-medium">Dates</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {selectedCompetitor.rate_summary.map((row) => (
                          <tr key={row.category_code}>
                            <td data-label="Category" className="px-3 py-2 text-gray-700 dark:text-gray-200">{categoryLabel(row.category_code)}</td>
                            <td data-label="Avg Rate" className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 tabular-nums">{fmtRate(row.avg_rate)}</td>
                            <td data-label="Avg Occupancy" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums"><InferredOccupancy value={row.avg_occupancy} /></td>
                            <td data-label="Dates" className="px-3 py-2 text-right text-gray-500 dark:text-gray-400 tabular-nums">
                              {typeof row.date_windows === "number" ? row.date_windows : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ThinScroll>
                </section>
              )}

              {status === "pending" && (
                <section className="rounded-lg ring-1 ring-amber-200 dark:ring-amber-500/20 bg-amber-50/60 dark:bg-amber-500/5 p-4">
                  <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
                    <svg viewBox="0 0 20 20" className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .2.08.39.22.53l3 3a.75.75 0 101.06-1.06L10.75 9.69V5z" clipRule="evenodd" />
                    </svg>
                    <div>
                      Rate data appears after this competitor's first sync.
                      {selectedCompetitor.first_data_at && (
                        <div className="mt-0.5 text-xs text-amber-700/80 dark:text-amber-300/80">
                          Tracking since {fmtAddedDate(selectedCompetitor.first_data_at)}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {status === "no_source" && (
                <section className="rounded-lg ring-1 ring-gray-200 dark:ring-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4 text-sm text-gray-600 dark:text-gray-300">
                  Not listed on Campspot, Airbnb, Hipcamp, or supported platforms — rates can't be tracked automatically.
                </section>
              )}
            </ThinScroll>
          </>
        )}
      </aside>
    </>
  );
};

export default CompetitorDetailDrawer;
